import { motion } from "framer-motion";
import { ChevronDown } from "lucide-react";

const LOGO = "/logo.png";

const fadeUp = {
  hidden: { opacity: 0, y: 50 },
  visible: (delay) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.9, delay: delay || 0, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero({ t }) {
  const scrollToAbout = () => {
    document.getElementById("about")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-[100svh] min-h-screen flex items-center justify-center overflow-hidden bg-[#050505]"
    >
      {/* Dark background with subtle gradient */}
      <div
        className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a] via-[#050505] to-[#030303]"
        aria-hidden="true"
      />

      {/* Subtle pattern overlay */}
      <div
        className="absolute inset-0 opacity-[0.02]"
        style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #D4AF37 1px, transparent 1px),
                           radial-gradient(circle at 75% 75%, #D4AF37 1px, transparent 1px)`,
          backgroundSize: "60px 60px",
        }}
        aria-hidden="true"
      />

      {/* Dark overlay */}
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />

      {/* Bottom fade to site bg */}
      <div
        className="absolute bottom-0 left-0 right-0 h-48 pointer-events-none"
        style={{ background: "linear-gradient(to top, #050505, transparent)" }}
        aria-hidden="true"
      />

      {/* Subtle noise grain */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          backgroundSize: "200px 200px",
        }}
        aria-hidden="true"
      />

      {/* Hero content */}
      <div className="relative z-10 text-center px-6 max-w-5xl mx-auto pt-24 pb-24">
        {/* Logo */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mb-6 sm:mb-8"
        >
          <img
            src={LOGO}
            alt="Orkhan Babayev Logo"
            className="w-24 h-24 sm:w-32 sm:h-32 md:w-40 md:h-40 mx-auto object-contain drop-shadow-2xl"
          />
        </motion.div>

        <motion.p
          custom={0.2}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-xs tracking-[0.28em] sm:tracking-[0.38em] uppercase text-[#D4AF37] font-body mb-6"
          data-testid="hero-caption"
        >
          {t.hero.caption}
        </motion.p>

        <motion.h1
          custom={0.38}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-[2.5rem] sm:text-6xl md:text-7xl lg:text-8xl font-bold text-white font-heading leading-[1.08] mb-8 break-words hyphens-auto"
          data-testid="hero-headline"
        >
          {t.hero.headline1}
          <br />
          {t.hero.headline2}
        </motion.h1>

        {/* Gold line separator */}
        <motion.div
          initial={{ scaleX: 0, opacity: 0 }}
          animate={{ scaleX: 1, opacity: 1 }}
          transition={{ duration: 0.9, delay: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="w-14 h-[2px] bg-[#D4AF37] mx-auto mb-8"
          style={{ transformOrigin: "left" }}
        />

        <motion.p
          custom={0.82}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-sm md:text-base text-white/70 font-body max-w-md mx-auto mb-10 leading-relaxed tracking-wide"
          data-testid="hero-subheadline"
        >
          {t.hero.subheadline}
        </motion.p>

        <motion.div
          custom={1.0}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          data-testid="hero-cta-group"
        >
          <a
            href="tel:+994555525001"
            className="w-full sm:w-auto px-8 sm:px-12 py-4 bg-[#D4AF37] text-black text-xs tracking-[0.28em] uppercase font-body font-bold hover:bg-[#F4CF57] transition-colors duration-300"
            data-testid="hero-primary-cta"
          >
            {t.nav.bookNow}
          </a>
          <a
            href="#services"
            onClick={(e) => {
              e.preventDefault();
              document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="w-full sm:w-auto px-8 sm:px-12 py-4 border border-white/30 text-white text-xs tracking-[0.28em] uppercase font-body font-bold hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-300"
            data-testid="hero-secondary-cta"
          >
            {t.hero.servicesBtn}
          </a>
        </motion.div>

        <motion.p
          custom={1.15}
          variants={fadeUp}
          initial="hidden"
          animate="visible"
          className="text-white/50 text-xs mt-10 font-body tracking-widest italic"
        >
          {t.hero.tagline}
        </motion.p>
      </div>

      {/* Scroll indicator */}
      <motion.button
        onClick={scrollToAbout}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 0.5 }}
        className="absolute bottom-6 left-1/2 -translate-x-1/2 flex flex-col items-center justify-center w-11 h-11 cursor-pointer z-10"
        data-testid="hero-scroll-indicator"
        aria-label={t.nav.scrollDown}
      >
        <ChevronDown className="text-[#D4AF37] animate-bounce" size={20} />
      </motion.button>
    </section>
  );
}
